import { Close, Delete as DeleteIcon, Sync } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { useUserStore } from '@/configs/store';
import { buttonSx } from '@/features/log/form/constants/styles';

import { CalDavCalendarList } from '../CalDavCalendarList';
import { CalDavDeleteConfirmDialog } from '../CalDavDeleteConfirmDialog';
import { CalDavInstructions } from '../CalDavInstructions';
import { CalDavSettingsForm } from '../CalDavSettingsForm';
import { CalDavStatusIndicator } from '../CalDavStatusIndicator';
import { useCalDavStore } from '../../store';

import s from './CalDavSettingsModal.module.scss';

interface CalDavSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export const CalDavSettingsModal: React.FC<CalDavSettingsModalProps> = observer(
  ({ open, onClose }) => {
    const store = useCalDavStore();
    const userStore = useUserStore();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    useEffect(() => {
      if (open) {
        store.fetchStatus();
        store.fetchConfig().then(() => {
          if (userStore.user?.email) {
            store.prefillLogin(userStore.user.email);
          }
        });
      }
    }, [open, store, userStore]);

    const handleOpenDeleteDialog = useCallback(() => {
      setDeleteDialogOpen(true);
    }, []);

    const handleCloseDeleteDialog = useCallback(() => {
      setDeleteDialogOpen(false);
    }, []);

    const handleDeleteSuccess = useCallback(() => {
      setDeleteDialogOpen(false);
      onClose();
    }, [onClose]);

    const handleSync = useCallback(async () => {
      try {
        await store.syncCalendars();
      } catch {
        // Error is handled in store
      }
    }, [store]);

    const handleSaveAndClose = useCallback(async () => {
      try {
        if (store.hasUnsavedChanges) {
          await store.saveCalendarSelection();
        }

        onClose();
      } catch {
        // Error is handled in store, don't close on error
      }
    }, [store, onClose]);

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          sx={{
            '.MuiPaper-root': {
              borderRadius: '12px',
              fontFamily: 'Montserrat, sans-serif',
              '& .MuiTypography-root': { fontFamily: 'inherit' },
              '& .MuiListItemText-primary': { fontFamily: 'inherit' },
              '& .MuiListItemText-secondary': { fontFamily: 'inherit' },
              '& .MuiFormLabel-root': { fontFamily: 'inherit' },
              '& .MuiInputBase-input': { fontFamily: 'inherit' },
              '& .MuiButton-root': { fontFamily: 'inherit' },
            },
          }}
        >
          <div className={s.caldav__header}>
            <div className={s.caldav__header_left}>
              <h1 className={s.caldav_title}>Настройки CalDAV</h1>
              <CalDavStatusIndicator
                status={store.integrationStatus}
                message={store.status?.message}
              />
            </div>
            <div className={s.caldav__header_actions}>
              {store.config && (
                <IconButton
                  color="error"
                  size="small"
                  onClick={handleOpenDeleteDialog}
                  title="Удалить интеграцию"
                >
                  <DeleteIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              )}
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </div>
          </div>
          <DialogContent>
            <Stack spacing={3}>
              <div>
                <p className={s.caldav__section_label}>
                  {store.config ? 'Настройки подключения' : 'Подключение к календарю Mail.ru'}
                </p>
                <CalDavInstructions isConfigured={!!store.config} />
                <CalDavSettingsForm />
              </div>

              {store.config && (
                <>
                  <Divider />
                  <div>
                    <div className={s.caldav__section_header}>
                      <p className={s.caldav__section_label}>Календари для синхронизации</p>
                      <Tooltip title="Обновить список календарей" arrow>
                        <LoadingButton
                          variant="outlined"
                          size="small"
                          onClick={handleSync}
                          loading={store.isSyncLoading}
                          startIcon={<Sync />}
                          disableElevation
                          sx={{
                            fontFamily: 'Montserrat, sans-serif',
                            textTransform: 'none',
                            color: 'var(--accent-color)',
                            border: '1px solid var(--accent-color)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              borderColor: 'var(--accent-hover-color)',
                            },
                            '&.Mui-disabled': {
                              borderColor: 'var(--secondary-color)',
                              color: 'var(--primary-color)',
                            },
                          }}
                        >
                          Обновить
                        </LoadingButton>
                      </Tooltip>
                    </div>
                    <CalDavCalendarList />
                  </div>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <div className={s.caldav__footer_buttons}>
              <Button
                sx={buttonSx}
                variant="outlined"
                onClick={onClose}
                disabled={store.isCalendarsLoading}
              >
                <p>Отмена</p>
              </Button>
              <LoadingButton
                onClick={handleSaveAndClose}
                variant="contained"
                loading={store.isCalendarsLoading}
                disabled={store.isCalendarsLoading}
                sx={{
                  ...buttonSx,
                  backgroundColor: 'var(--accent-color)',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--accent-hover-color)',
                  },
                }}
              >
                <p>Сохранить и закрыть</p>
              </LoadingButton>
            </div>
          </DialogActions>
        </Dialog>

        <CalDavDeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onSuccess={handleDeleteSuccess}
        />
      </>
    );
  },
);
